<template>
  <div>
    <breadcrumbs
      :with-homepage="true"
      :routes="[]"
      active-route="Vertrag widerrufen"
    />
    <section class="section-withdrawal col-layout container">
      <div class="row">
        <div class="col-xs-12">
          <h1 class="page-title h2">
            Vertrag widerrufen
          </h1>
        </div>

        <div class="col-xs-12 col-sm-7">
          <p class="intro">
            Sie können hier Ihren Vertrag gemäß den gesetzlichen Vorgaben elektronisch widerrufen.
            Bitte füllen Sie die folgenden Pflichtfelder aus.
          </p>

          <form
            v-if="!success"
            @submit.prevent="onPrimarySubmit"
            novalidate="novalidate"
          >
            <template v-if="step === 1">
              <base-input
                type="text"
                name="name"
                autocomplete="name"
                required="required"
                v-model="name"
                placeholder="Ihr Name *"
              />

              <base-input
                type="text"
                name="contractReference"
                autocomplete="off"
                required="required"
                v-model="contractReference"
                placeholder="Bestellnummer *"
              />

              <base-input
                type="email"
                name="email"
                autocomplete="email"
                required="required"
                v-model="email"
                placeholder="E-Mail-Adresse, die zur Bestellung verwendet wurde *"
              />

              <base-checkbox
                id="withdrawal-privacy"
                v-model="privacy"
              >
                Ja, ich stimme der Verarbeitung meiner Daten gemäß der
                <router-link :to="localizedRoute('/datenschutzerklaerung')">
                  Datenschutzerklärung
                </router-link>
                zu.
              </base-checkbox>

              <br>

              <div
                v-if="error"
                class="error-message"
              >
                {{ error }}
              </div>

              <div class="clearfix action">
                <p class="pull-left required-fields">
                  * Pflichtfelder
                </p>

                <button
                  type="submit"
                  class="btn btn-primary pull-right"
                  :disabled="loading"
                >
                  Vertrag widerrufen
                </button>
              </div>
            </template>

            <template v-else>
              <div class="confirmation-summary">
                <h2 class="h4">
                  Bitte prüfen Sie Ihre Widerrufserklärung
                </h2>
                <dl>
                  <dt>Name</dt>
                  <dd>{{ name }}</dd>
                  <dt>Bestellnummer</dt>
                  <dd>{{ contractReference }}</dd>
                  <dt>E-Mail-Adresse für Eingangsbestätigung</dt>
                  <dd>{{ email }}</dd>
                </dl>
              </div>

              <div class="clearfix action">
                <div class="confirmation-actions pull-right">
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    :disabled="loading"
                    @click="step = 1"
                  >
                    Angaben ändern
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary"
                    :disabled="loading"
                    @click="confirmWithdrawal"
                  >
                    Widerruf bestätigen
                  </button>
                </div>
              </div>

              <div
                v-if="error"
                class="error-message"
              >
                {{ error }}
              </div>
            </template>
          </form>

          <div
            v-else
            class="success-message"
          >
            <h2 class="h3">
              Widerruf übermittelt
            </h2>
            <p>
              Ihre Widerrufserklärung wurde über die Widerrufsfunktion versandt.
              Eine Eingangsbestätigung wird an die von Ihnen angegebene E-Mail-Adresse gesendet.
            </p>
          </div>
        </div>

        <div class="col-xs-12 col-sm-5">
          <aside class="info">
            <h2 class="h4">
              Hinweise zur elektronischen Widerrufsfunktion
            </h2>
            <ul>
              <li>Sie können diese Funktion während der gesamten Widerrufsfrist nutzen.</li>
              <li>Stellen Sie sicher, dass die Vertragsdaten (z. B. Bestellnummer) korrekt sind.</li>
              <li>Die Eingangsbestätigung wird an die angegebene E-Mail-Adresse gesendet.</li>
            </ul>
          </aside>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import Breadcrumbs from '../components/core/Breadcrumbs'
import BaseInput from 'theme/components/core/blocks/Form/BaseInput.vue'
import BaseCheckbox from 'theme/components/core/blocks/Form/BaseCheckbox.vue'
import { localizedRoute } from '@vue-storefront/core/lib/multistore'
import { magentoRestV1BaseUrl } from '@vue-storefront/core/helpers/magentoRestUrl'

export default {
  name: 'WithdrawalPage',
  components: {
    Breadcrumbs,
    BaseInput,
    BaseCheckbox
  },
  computed: {
    magentoApiBaseUrl () {
      return magentoRestV1BaseUrl()
    }
  },
  data () {
    return {
      name: '',
      contractReference: '',
      email: '',
      privacy: false,
      step: 1,
      loading: false,
      error: '',
      success: false
    }
  },
  methods: {
    localizedRoute,
    validateFields () {
      if (!this.name || !this.contractReference || !this.email) {
        this.error = 'Bitte füllen Sie alle Pflichtfelder aus.'
        return false
      }
      if (!this.privacy) {
        this.error = 'Bitte bestätigen Sie den Hinweis zur Datenverarbeitung.'
        return false
      }
      this.error = ''
      return true
    },
    async postWithdrawal (path) {
      const url = `${this.magentoApiBaseUrl}/${path}`
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          email: this.email,
          orderNumber: this.contractReference
        })
      })
    },
    async onPrimarySubmit () {
      if (!this.validateFields()) return

      this.loading = true
      this.error = ''

      try {
        const response = await this.postWithdrawal('withdrawal/can-withdraw')

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Für diese Kombination aus Bestellnummer und E-Mail-Adresse wurde keine Bestellung gefunden.')
          }
          throw new Error('Die angegebenen Daten konnten nicht geprüft werden. Bitte versuchen Sie es erneut.')
        }

        const value = await response.json()
        const canWithdraw = value === true
        if (!canWithdraw) {
          throw new Error('Für diese Kombination aus Bestellnummer und E-Mail-Adresse wurde keine Bestellung gefunden.')
        }

        this.step = 2
      } catch (e) {
        this.error = e.message || 'Es ist ein unbekannter Fehler aufgetreten.'
        this.$store.dispatch('notification/spawnNotification', {
          type: 'error',
          message: this.error,
          action1: { label: this.$t('OK') }
        })
      } finally {
        this.loading = false
      }
    },
    async confirmWithdrawal () {
      if (!this.validateFields()) return

      this.loading = true
      this.error = ''

      try {
        const response = await this.postWithdrawal('withdrawal/confirmation')

        if (!response.ok) {
          throw new Error('Fehler beim Übermitteln des Widerrufs. Bitte versuchen Sie es erneut.')
        }

        this.success = true
        this.loading = false

        this.$store.dispatch('notification/spawnNotification', {
          type: 'success',
          message: 'Ihre Widerrufserklärung wurde erfolgreich übermittelt.',
          action1: { label: this.$t('OK') }
        })
      } catch (e) {
        this.loading = false
        this.error = e.message || 'Es ist ein unbekannter Fehler aufgetreten.'
        this.$store.dispatch('notification/spawnNotification', {
          type: 'error',
          message: this.error,
          action1: { label: this.$t('OK') }
        })
      }
    }
  }
}
</script>

<style scoped>
.section-withdrawal {
  margin-bottom: 40px;
}

.intro {
  margin-bottom: 20px;
}

.action {
  margin-top: 10px;
}

.confirmation-summary {
  margin-top: 30px;
  padding: 20px;
  border: 1px solid #e0e0e0;
}

.confirmation-summary dl {
  margin: 0;
}

.confirmation-summary dt {
  font-weight: 600;
  margin-top: 10px;
}

.confirmation-summary dd {
  margin: 0;
}

.confirmation-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}

.section-withdrawal .btn-primary {
  width: auto;
  min-width: 290px;
  max-width: 100%;
  white-space: nowrap;
}

.error-message {
  color: #d0021b;
  margin-bottom: 10px;
}

.success-message {
  padding: 20px;
  border: 1px solid #e0e0e0;
}

.info {
  margin-top: 30px;
}

@media (min-width: 768px) {
  .info {
    margin-top: 60px;
  }
}
</style>
